// Vendor Program v2 PR 3 — the VFI Accounting staff page at
// /portfolio-admin/vfi-accounting (sidebar "Vendors" group, gated
// VendorBilling.Read at the route). Top-to-bottom for the five-second
// read: dashboard tile strip → reports awaiting review → draft invoice
// pool → formal accounting reports. Drawers are URL-backed so every
// surface is deep-linkable: ?report=<id> (report review), ?draft=<id>
// (draft audit), ?acct=<tab> (accounting report tab).

import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/design-system/components/ui";
import { VfiSummaryTiles } from "@/components/portfolioAdmin/vendors/accounting/VfiSummaryTiles";
import { SubmittedReportsQueue } from "@/components/portfolioAdmin/vendors/accounting/SubmittedReportsQueue";
import { DraftInvoicesSection } from "@/components/portfolioAdmin/vendors/accounting/DraftInvoicesSection";
import { DraftInvoiceAuditDrawer } from "@/components/portfolioAdmin/vendors/accounting/DraftInvoiceAuditDrawer";
import {
  AccountingReportsSection,
  type AccountingTabId,
} from "@/components/portfolioAdmin/vendors/accounting/AccountingReportsSection";
import { VendorReportReviewDrawer } from "@/components/portfolioAdmin/vendors/reports/VendorReportReviewDrawer";

const ACCT_TABS: AccountingTabId[] = [
  "past-income",
  "aging",
  "in-transit",
  "outstanding",
];

export default function VfiAccountingPage() {
  const [params, setParams] = useSearchParams();
  const openReportId = params.get("report");
  const openDraftId = params.get("draft");
  const acctParam = params.get("acct");
  const acctTab: AccountingTabId = ACCT_TABS.includes(acctParam as AccountingTabId)
    ? (acctParam as AccountingTabId)
    : "past-income";

  const setParam = (key: string, value: string | null) => {
    const updated = new URLSearchParams(params);
    if (value) updated.set(key, value);
    else updated.delete(key);
    setParams(updated, { replace: true });
  };

  // One drawer at a time: opening the report review closes the draft
  // audit (and vice versa) so back/escape behavior stays predictable.
  const openReport = (reportId: string) => {
    const updated = new URLSearchParams(params);
    updated.delete("draft");
    updated.set("report", reportId);
    setParams(updated, { replace: true });
  };

  const scrollToReportsQueue = () =>
    document
      .getElementById("vfi-reports-queue-anchor")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="flex flex-col gap-space-4" data-testid="vfi-accounting-page">
      <PageHeader
        title="VFI Accounting"
        subtitle="Vendor Fee Income — review monthly reports, send invoices, and run receivables."
      />

      <VfiSummaryTiles onGoToReportsQueue={scrollToReportsQueue} />

      <div id="vfi-reports-queue-anchor">
        <SubmittedReportsQueue onOpenReport={openReport} />
      </div>

      <DraftInvoicesSection
        onOpenDraft={(id) => setParam("draft", id)}
        onOpenReport={openReport}
      />

      <AccountingReportsSection
        activeTab={acctTab}
        onTabChange={(tab) =>
          setParam("acct", tab === "past-income" ? null : tab)
        }
      />

      {openReportId && (
        <VendorReportReviewDrawer
          reportId={openReportId}
          open={!!openReportId}
          onClose={() => setParam("report", null)}
        />
      )}

      {openDraftId && (
        <DraftInvoiceAuditDrawer
          invoiceId={openDraftId}
          open={!!openDraftId}
          onClose={() => setParam("draft", null)}
          onOpenReport={openReport}
        />
      )}
    </div>
  );
}
